import { LanguageEnum } from "@shared/enums/language.enum";
import { Router, NavigationEnd } from "@angular/router";
import { Location } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { KeycloakService } from "keycloak-angular";
import { Observable, throwError } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { CoreConfig } from "../core-configuration/core-config";
import { CORE_CONFIG } from "../core-configuration/core-config.token";
import { SidebarNavItem } from "../layout";
import { NavigationResponse } from "./NavigationResponse";
import { FeatureToggleService } from "@core/services/feature-toggle.service";

@Injectable({
  providedIn: "root"
})
export class NavigationService {
  sidebarLinks: SidebarNavItem[] = [];
  userRoles: Array<string>;
  private history: string[] = [];

  constructor(
    private http: HttpClient,
    @Inject(CORE_CONFIG) private config: CoreConfig,
    private keyclockService: KeycloakService,
    private router: Router,
    private featureToggleService: FeatureToggleService
  ) {
    this.userRoles = this.keyclockService.getUserRoles();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.history.push(event.urlAfterRedirects);
      }
    });
  }

  getNavigationItems(lang: LanguageEnum): Observable<SidebarNavItem[]> {
    if (!this.config || !this.config.endpointNavigation) {
      return throwError(
        "NavigationService: no endpointNavigation provided in CORE_CONFIG"
      );
    }

    return this.getEndpointNavigation(lang).pipe(
      switchMap((normalizedUrl) =>
        this.http.get(normalizedUrl).pipe(
          map((links: NavigationResponse) => {
            if (Array.isArray(links)) {
              this.sidebarLinks = links.filter((link) => {
                if (link.items && link.items.length) {
                  link.items = link.items.filter((childLink) => {
                    if (childLink.roles && childLink.roles.length) {
                      return childLink.roles.find(
                        (role) => this.userRoles.indexOf(role) >= 0
                      );
                    } else {
                      return childLink;
                    }
                  });
                }
                return link.roles.find(
                  (role) => this.userRoles.indexOf(role) >= 0
                );
              });
            } else {
              this.sidebarLinks = links.results.filter((link) =>
                link.roles.find((role) => this.userRoles.indexOf(role) >= 0)
              );
            }
            return this.sidebarLinks;
          })
        )
      )
    );
  }

  private getEndpointNavigation(lang: LanguageEnum): Observable<string> {
    const endpoints = {
      [LanguageEnum.PT_BR]: assetUrl(this.config.endpointNavigation),
      [LanguageEnum.ES]: assetUrl(this.config.endpointNavigationEsp),
      [LanguageEnum.EN_US]: assetUrl(this.config.endpointNavigationUs),
      [LanguageEnum.DE_DE]: assetUrl(this.config.endpointNavigationDe)
    };

    const endpointsFleetality = {
      [LanguageEnum.DE_DE]: assetUrl(
        this.config.endpointNavigationDeFleetality
      ),
      [LanguageEnum.PT_BR]: assetUrl(this.config.endpointNavigationFleetality),
      [LanguageEnum.ES]: assetUrl(this.config.endpointNavigationEspFleetality),
      [LanguageEnum.EN_US]: assetUrl(this.config.endpointNavigationEnFleetality)
    };

    return this.isFleetality().pipe(
      map((isFleet) => {
        if (isFleet) {
          return (
            endpointsFleetality[lang] ||
            assetUrl(this.config.endpointNavigationFleetality)
          );
        }
        return endpoints[lang] || assetUrl(this.config.endpointNavigation);
      })
    );
  }

  isFleetality(): Observable<boolean> {
    return this.featureToggleService
      .verifyFeatureToggle()
      .pipe(map((res) => res.changeDefaultPlatformIcon));
  }

  canBackByLocation(): boolean {
    this.history.pop();
    return this.history.length > 0;
  }
}
function assetUrl(endpointNavigation: string) {
  throw new Error("Function not implemented.");
}
